import { DestroyRef, Directive, effect, ElementRef, inject, input, NgZone, type OnInit, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type SavedTableSettings, SessionStorageService } from '@core';
import { fromEvent } from 'rxjs';

const MIN_COLUMN_WIDTH = 50;
const MAX_COLUMN_WIDTH = 600;

/**
 * Makes a table column resizable
 *
 * ```html
 * <th
 *   [avsColumnResize]="isResizable"
 *   [avsColumnResizeColumn]="column.id"
 *   [avsColumnResizeTable]="tableId"
 *   mat-header-cell
 * >
 * ```
 */
@Directive({
  selector: 'th[avsColumnResize]',
})
export class DatagridColumnResizeDirective implements OnInit {
  enabled = input(true, { alias: 'avsColumnResize' });
  tableId = input<string | null>(null, { alias:  'avsColumnResizeTable' });
  columnId = input<string | null>(null, { alias: 'avsColumnResizeColumn' });

  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private zone = inject(NgZone);
  private sessionStorageService = inject(SessionStorageService);
  private destroyRef = inject(DestroyRef);

  private isResizing = false;
  private startX!: number;
  private startWidth!: number;
  private width: number | null = null;
  private column: HTMLElement;
  private resizer!: HTMLElement;

  constructor() {
    this.column = this.el.nativeElement;

    effect(() => {
      const enabled = this.enabled();

      if (this.resizer) {
        this.toggleResizer(enabled);
      }
    });

    effect(() => this.applySavedWidth());
  }

  ngOnInit() {
    this.createResizer();
    this.initializeResizeListener();
    this.toggleResizer(this.enabled());
  }

  private createResizer() {
    this.resizer = this.renderer.createElement('div');
    this.renderer.addClass(this.resizer, 'avs-column-resizer');
    this.renderer.setAttribute(this.resizer, 'role', 'separator');
    this.renderer.setAttribute(this.resizer, 'aria-orientation', 'vertical');
    this.renderer.appendChild(this.column, this.resizer);
  }

  private toggleResizer(enabled: boolean) {
    if (enabled) {
      this.renderer.appendChild(this.column, this.resizer);
    } else {
      this.renderer.removeChild(this.column, this.resizer);
    }
  }

  private initializeResizeListener() {
    this.zone.runOutsideAngular(() => {
      fromEvent(this.resizer, 'mousedown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e: Event) => this.onMouseDown(e as MouseEvent));

      fromEvent(document, 'mousemove')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e: Event) => this.isResizing && this.onMouseMove(e as MouseEvent));

      fromEvent(document, 'mouseup')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.isResizing && this.onMouseUp());
    });
  }

  private onMouseDown(e: MouseEvent) {
    e.preventDefault();
    this.isResizing = true;
    this.startX = e.pageX;
    this.startWidth = this.column.offsetWidth;
    this.width = null;
  }

  private onMouseMove(e: MouseEvent) {
    const width = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, this.startWidth + (e.pageX - this.startX)));

    this.width = width;
    this.renderer.setStyle(this.column, 'width', `${width}px`);
    this.renderer.setStyle(this.column, 'min-width', `${width}px`);
  }

  private onMouseUp() {
    this.isResizing = false;

    // a click without a drag leaves the column auto-sized.
    if (this.width === null) {
      return;
    }

    this.saveWidth(this.width);

    // a drag released away from the resizer fires its click on the header, which would toggle sorting
    this.swallowNextClick();
  }

  private swallowNextClick() {
    const swallowClick = (click: Event) => click.stopPropagation();

    document.addEventListener('click', swallowClick, {
      capture: true,
      once: true,
    });
    setTimeout(() => document.removeEventListener('click', swallowClick, {
      capture: true,
    }));
  }

  private applySavedWidth() {
    const width = this.savedWidths()?.[this.columnId() ?? ''];

    if (width) {
      this.renderer.setStyle(this.column, 'width', `${width}px`);
      this.renderer.setStyle(this.column, 'min-width', `${width}px`);
    } else {
      this.renderer.removeStyle(this.column, 'width');
      this.renderer.removeStyle(this.column, 'min-width');
    }
  }

  private savedWidths() {
    const tableId = this.tableId();

    return tableId ? this.sessionStorageService.getItem<SavedTableSettings>(tableId)?.widths : null;
  }

  private saveWidth(width: number) {
    const tableId = this.tableId();
    const columnId = this.columnId();

    if (tableId && columnId) {
      this.sessionStorageService.saveColumnWidths(tableId, {
        ...this.savedWidths(),
        [columnId]: width,
      });
    }
  }
}
